/**
 * Match end: the headline on the felt, the whole match on the pad, one row per
 * hand. The pad is the only light surface in the app and it is an object, not a
 * theme — a scorepad placed on the table.
 */
import { rematchStanding, type RematchStanding } from '@cards/shared'
import { countWord, signed } from './atoms'
import type { TableView } from './view'

export function MatchEnd({
  view,
  onRematchChoice,
  onReplay,
  onLeave,
  onFairness,
  locked,
}: {
  view: TableView
  onRematchChoice: (isIn: boolean) => void
  onReplay: () => void
  onLeave: () => void
  onFairness: () => void
  locked: boolean
}): JSX.Element {
  const match = view.match
  const status = match?.status
  const won = status?.kind === 'finished' && status.winningTeam === view.myTeam
  const room = view.snapshot.room
  const standings = room.seats.map((_, seat) => ({ seat, standing: rematchStanding(room, seat) }))
  const mine = view.mySeat === null ? null : rematchStanding(room, view.mySeat)
  const hands = match?.handHistory ?? []
  const partner = view.seats.find((s) => s.isPartner)
  const otherTeam = view.myTeam === 0 ? 1 : 0

  return (
    <div className="end">
      <div className="end-head">
        <h1>{status?.kind === 'finished' && status.winningTeam === null ? 'Match over' : won ? 'You win' : 'You lose'}</h1>
        <p>
          <b className={view.scores.us < 0 ? 'minus' : undefined}>{signed(view.scores.us)}</b> to{' '}
          <b className={view.scores.them < 0 ? 'minus' : undefined}>{signed(view.scores.them)}</b>
          {partner ? ` with ${partner.name}` : ''}, over {countWord(hands.length)} hand
          {hands.length === 1 ? '' : 's'}
        </p>
      </div>

      <div className="end-record">
        <div className="pad cols">
          <div className="h">Hand</div>
          <div className="h r" style={{ fontStretch: '100%', fontWeight: 500, fontSize: 12 }}>
            Us
          </div>
          <div className="h r" style={{ fontStretch: '100%', fontWeight: 500, fontSize: 12 }}>
            Them
          </div>
          {hands.map((hand) => {
            const us = hand.deltas[view.myTeam ?? 0] ?? 0
            const them = hand.deltas[otherTeam] ?? 0
            return (
              <Row
                key={hand.index}
                no={hand.index + 1}
                label={view.gameUi.contractLabelNode(hand.contractLabel)}
                by={hand.contractorSeat === null ? '' : view.name(hand.contractorSeat)}
                made={hand.contractMade}
                us={us}
                them={them}
              />
            )
          })}
          <div style={{ gridColumn: '1/-1', height: 0, borderTop: '1.5px solid var(--record-text)' }} />
          <div className="k">
            <b>Final</b>
          </div>
          <div className={`r${view.scores.us < 0 ? ' minus' : ''}`} style={{ fontSize: 17 }}>
            {signed(view.scores.us)}
          </div>
          <div className={`r${view.scores.them < 0 ? ' minus' : ''}`} style={{ fontSize: 17 }}>
            {signed(view.scores.them)}
          </div>
        </div>

        {/* Not "Shuffle verified": whether a hand checks out is decided on the
            player's device, per hand, and for a table of bots the answer is
            "committed beforehand" rather than "verified". The claim belongs on
            the fairness screen, where it can be qualified. */}
        <button className="fair" onClick={onFairness}>
          Check how the cards were shuffled
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <ul className="rematch" aria-label="Who is in for another match">
          {standings.map(({ seat, standing }) => (
            <li key={seat} className={`rematch-seat ${standing}`}>
              <span className="rematch-name">{seat === view.mySeat ? 'You' : view.name(seat)}</span>
              <span className="rematch-state">{STANDING_LABEL[standing]}</span>
            </li>
          ))}
        </ul>
        <p className="cta-note" role="status">
          {rematchNote(view, standings)}
        </p>
        {mine === null ? null : (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
            <button
              className="btn btn-primary"
              style={{ height: 50 }}
              aria-pressed={mine === 'in'}
              onClick={() => onRematchChoice(true)}
              disabled={locked || mine === 'in' || mine === 'left' || standings.some((s) => s.standing === 'left')}
            >
              {mine === 'in' ? 'You’re in' : 'I’m in, play again'}
            </button>
            <button
              className="btn btn-secondary"
              style={{ height: 50 }}
              aria-pressed={mine === 'out'}
              onClick={() => onRematchChoice(false)}
              disabled={locked || mine === 'out' || mine === 'left'}
            >
              {mine === 'out' ? 'You’re out' : 'I’m out'}
            </button>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onReplay}>
            Replay the match
          </button>
          <button className="btn btn-quiet" style={{ height: 46 }} onClick={onLeave}>
            Leave the table
          </button>
        </div>
      </div>
    </div>
  )
}

const STANDING_LABEL: Record<RematchStanding, string> = {
  in: 'In',
  out: 'Out',
  undecided: 'Deciding',
  left: 'Left',
}

/** The one sentence under the choices: what is standing between this table and
 *  the next deal. Somebody leaving is final — the seats are no longer the same
 *  seats — so it outranks everything else. */
function rematchNote(
  view: TableView,
  standings: readonly { seat: number; standing: RematchStanding }[],
): string {
  const who = (s: RematchStanding): string[] =>
    standings.filter((x) => x.standing === s).map((x) => (x.seat === view.mySeat ? 'you' : view.name(x.seat)))
  const left = who('left')
  if (left.length > 0) {
    return `${capitalise(list(left))} left the table, so these seats can’t play again.`
  }
  const out = who('out')
  if (out.length > 0) {
    const verb = out.length === 1 && out[0] !== 'you' ? 'is' : 'are'
    return `${capitalise(list(out))} ${verb} out. Everyone has to be in to play again.`
  }
  const waiting = who('undecided')
  if (waiting.length > 0) return `Waiting for ${list(waiting)}. It deals once everyone is in.`
  return 'Everyone’s in. Dealing…'
}

function list(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function Row({
  no,
  label,
  by,
  made,
  us,
  them,
}: {
  no: number
  label: React.ReactNode
  by: string
  made: boolean | null
  us: number
  them: number
}): JSX.Element {
  return (
    <>
      <div className="k">
        <span className="hand-no">{no}</span>
        <b>{label}</b>
        <span>{by}</span>
        {made === null ? null : <span className={made ? 'made' : 'set'}>{made ? 'made' : 'set'}</span>}
      </div>
      <Cell v={us} />
      <Cell v={them} />
    </>
  )
}

function Cell({ v }: { v: number }): JSX.Element {
  if (v === 0) return <div className="r" style={{ color: 'var(--record-text-3)' }}>0</div>
  return <div className={`r${v < 0 ? ' minus' : ''}`}>{v < 0 ? signed(v) : `+${v}`}</div>
}
